import { ArrowLeft, Download, Flower2, RefreshCw, ThumbsUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AudienceWelcome } from '../components/AudienceWelcome';
import { LoadingTopics, StatusPanel } from '../components/StatusPanel';
import { useEvent } from '../context/EventContext';
import { rankTopics } from '../lib/ranking';
import { exportEventResults } from '../lib/exportResults';
import type { LeaderboardSort } from '../types';

export function LeaderboardView() {
  const { snapshot, loading, error, online, pendingFlowersFor, refresh } = useEvent();
  const [sort, setSort] = useState<LeaderboardSort>('default');
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const exportSnapshot = useMemo(
    () =>
      snapshot
        ? {
            ...snapshot,
            topics: snapshot.topics.map((topic) => ({
              ...topic,
              flowerCount: topic.flowerCount + pendingFlowersFor(topic.id),
            })),
          }
        : null,
    [pendingFlowersFor, snapshot],
  );
  const ranked = useMemo(
    () => rankTopics(exportSnapshot?.topics ?? [], sort),
    [exportSnapshot?.topics, sort],
  );

  if (loading && !snapshot) return <LoadingTopics />;
  if (!snapshot) {
    return (
      <StatusPanel
        title="Leaderboard unavailable"
        message={error ?? 'No event data was returned.'}
        action={() => void refresh()}
      />
    );
  }
  if (!snapshot.settings.audienceBrowsingEnabled) {
    return <AudienceWelcome currentTopicId={snapshot.settings.currentTopicId} />;
  }

  const exportResults = async () => {
    setExporting(true);
    setExportStatus(null);
    try {
      await exportEventResults(exportSnapshot ?? snapshot);
      setExportStatus('Excel export downloaded.');
    } catch {
      setExportStatus('Export failed. Check your connection and try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page leaderboard-page">
      <Link className="back-link" to="/">
        <ArrowLeft size={19} />
        Running order
      </Link>
      <header className="leaderboard-header">
        <h1>Room response</h1>
        <div className="leaderboard-actions">
          <button
            className="button secondary export-button"
            onClick={() => void exportResults()}
            disabled={exporting || !online}
          >
            <Download size={18} aria-hidden="true" />
            {exporting ? 'Preparing…' : 'Export results'}
          </button>
          <button
            className="icon-button"
            onClick={() => void refresh()}
            aria-label="Refresh leaderboard"
          >
            <RefreshCw />
          </button>
        </div>
      </header>
      {exportStatus && (
        <div className="inline-status" role="status" aria-live="polite">
          {exportStatus}
        </div>
      )}

      <div className="segmented-control" aria-label="Leaderboard sort">
        {[
          ['default', 'Overall'],
          ['likes', 'Most liked'],
          ['flowers', 'Most flowers'],
        ].map(([value, label]) => (
          <button
            key={value}
            className={sort === value ? 'is-active' : ''}
            onClick={() => setSort(value as LeaderboardSort)}
            aria-pressed={sort === value}
          >
            {label}
          </button>
        ))}
      </div>

      <ol className="leaderboard-list">
        {ranked.map((topic, index) => (
          <li
            key={topic.id}
            className={snapshot.settings.currentTopicId === topic.id ? 'is-current' : ''}
          >
            <span className="rank">{String(index + 1).padStart(2, '0')}</span>
            <div className="leaderboard-topic">
              {snapshot.settings.currentTopicId === topic.id && (
                <span className="current-topic-label">Now presenting</span>
              )}
              <Link to={`/topics/${topic.id}`}>{topic.title}</Link>
              <span>{topic.presenter}</span>
            </div>
            <dl>
              <div>
                <ThumbsUp size={16} />
                <dt>Likes</dt>
                <dd>{topic.likeCount.toLocaleString()}</dd>
              </div>
              <div>
                <Flower2 size={16} />
                <dt>Flowers</dt>
                <dd>{topic.flowerCount.toLocaleString()}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ol>
      <p className="leaderboard-note" role="status">
        Updated on entry or manual refresh. Shared counts may take a few seconds to
        settle.
      </p>
    </div>
  );
}
